// Activo Fijo → Catálogos → Custodios / responsables.
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Pencil, Plus, Trash2, UserCheck } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { getErrorMessage } from '@/lib/api/client';
import { Campo, Custodio, Modal, Switch, af, fieldCls } from './shared';

const vacio = { codigo: '', nombre: '', identificacion: '', cargo: '', email: '', es_activo: true };

export function CustodiosAF() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const { data: rows = [], isFetching } = useQuery<Custodio[]>({
    queryKey: ['af-custodios'],
    queryFn: () => af.list<Custodio>('custodios', '?select=*&deleted_at=is.null&order=nombre'),
  });
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<typeof vacio>(vacio);
  const [abierto, setAbierto] = useState(false);
  const [fNombre, setFNombre] = useState('');
  const set = (p: Partial<typeof vacio>) => setForm((f) => ({ ...f, ...p }));
  const filtrados = useMemo(() => rows.filter((c) => !fNombre.trim() || c.nombre.toLowerCase().includes(fNombre.trim().toLowerCase()) || (c.identificacion ?? '').includes(fNombre.trim())), [rows, fNombre]);

  const abrir = (c?: Custodio) => {
    if (c) setForm({ codigo: c.codigo ?? '', nombre: c.nombre, identificacion: c.identificacion ?? '', cargo: c.cargo ?? '', email: c.email ?? '', es_activo: c.es_activo });
    else setForm(vacio);
    setEditId(c?.id ?? null);
    setAbierto(true);
  };
  const guardar = useMutation({
    mutationFn: () => {
      const body = { codigo: form.codigo || null, nombre: form.nombre.trim(), identificacion: form.identificacion || null, cargo: form.cargo || null, email: form.email || null, es_activo: form.es_activo };
      return editId ? af.update('custodios', editId, body) : af.create('custodios', body);
    },
    onSuccess: () => { toast.success('Custodio guardado'); setAbierto(false); void qc.invalidateQueries({ queryKey: ['af-custodios'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const eliminar = useMutation({
    mutationFn: (id: string) => af.remove('custodios', id),
    onSuccess: () => { toast.success('Custodio eliminado'); void qc.invalidateQueries({ queryKey: ['af-custodios'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="p-6 space-y-4">
      {dlg.ui}
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-xl font-semibold flex items-center gap-2"><UserCheck className="w-5 h-5" /> Custodios / responsables</h1>
        <Button onClick={() => abrir()}><Plus className="w-4 h-4 mr-1" /> Nuevo</Button>
      </div>
      <p className="text-sm text-muted-foreground">Personas responsables de los activos (para actas de entrega-recepción y control de faltantes).</p>
      <input className={`${fieldCls} w-64`} placeholder="Filtrar por nombre o cédula…" value={fNombre} onChange={(e) => setFNombre(e.target.value)} />
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Custodios ({filtrados.length})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">Nombre</th><th className="py-2 pr-3">Identificación</th><th className="py-2 pr-3">Cargo</th><th className="py-2 pr-3">Email</th><th className="py-2 pr-3">Estado</th><th className="py-2"></th>
            </tr></thead>
            <tbody>
              {filtrados.length === 0 && !isFetching && <tr><td colSpan={6} className="py-8 text-center text-muted-foreground">Sin custodios</td></tr>}
              {filtrados.map((c) => (
                <tr key={c.id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3 font-medium">{c.nombre}</td>
                  <td className="py-2 pr-3 font-mono">{c.identificacion ?? '—'}</td>
                  <td className="py-2 pr-3">{c.cargo ?? '—'}</td>
                  <td className="py-2 pr-3 text-muted-foreground">{c.email ?? '—'}</td>
                  <td className="py-2 pr-3">{c.es_activo ? 'Activo' : 'Inactivo'}</td>
                  <td className="py-2 text-right whitespace-nowrap">
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Editar" onClick={() => abrir(c)}><Pencil className="w-4 h-4" /></button>
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-destructive hover:bg-destructive/10 ml-1" title="Eliminar" onClick={async () => { if (await dlg.confirmar(`¿Eliminar "${c.nombre}"?`, { peligro: true })) eliminar.mutate(c.id); }}><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {abierto && (
        <Modal title={editId ? 'Editar custodio' : 'Nuevo custodio'} onClose={() => setAbierto(false)}>
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-3">
              <Campo label="Código"><input className={fieldCls} value={form.codigo} onChange={(e) => set({ codigo: e.target.value.toUpperCase() })} /></Campo>
              <Campo label="Nombre *" className="col-span-2"><input className={fieldCls} value={form.nombre} onChange={(e) => set({ nombre: e.target.value })} /></Campo>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Campo label="Identificación"><input className={fieldCls} value={form.identificacion} onChange={(e) => set({ identificacion: e.target.value })} /></Campo>
              <Campo label="Cargo"><input className={fieldCls} value={form.cargo} onChange={(e) => set({ cargo: e.target.value })} /></Campo>
            </div>
            <Campo label="Email"><input type="email" className={fieldCls} value={form.email} onChange={(e) => set({ email: e.target.value })} /></Campo>
            <Switch checked={form.es_activo} onChange={(v) => set({ es_activo: v })} label="Activo" />
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setAbierto(false)}>Cancelar</Button>
              <Button isLoading={guardar.isPending} disabled={!form.nombre.trim()} onClick={() => guardar.mutate()}>Guardar</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
